/**
 * GraphQL documents for Phase 2 split search queries + overview stream.
 *
 * Three split queries (content, playlists, profiles) fire in parallel.
 * OVERVIEW_STREAM_SUBSCRIPTION subscribes to Brooklyn's searchOverviewStream
 * for the Sage narrative. Requires chatSessionId from the aiSearch response (Brooklyn
 * loads the seeded turn context from the DB by that id).
 */

import { gql } from '@apollo/client'
import { PLAYLIST_ITEM_CONTENT_FIELDS } from '../fragments'

// ── Split queries (parallel from frontend) ─────────────────────────

export const SEARCH_QUERY = gql`
  query Search(
    $query: String
    $language: String
    $contentLimit: Int
    $contentOffset: Int
    $sessionId: ID
    $filters: SearchFilters
    $sort: SearchSort
    $skipOverview: Boolean
    $previousChipTexts: [String!]
    $previousQuery: String
    $previousContentIds: [Int!]
  ) {
    search(
      query: $query
      language: $language
      contentLimit: $contentLimit
      contentOffset: $contentOffset
      sessionId: $sessionId
      filters: $filters
      sort: $sort
      skipOverview: $skipOverview
      previousChipTexts: $previousChipTexts
      previousQuery: $previousQuery
      previousContentIds: $previousContentIds
    ) {
      resultIds
      contentTotalCount
      topContentIds
      topContentScores
      entities {
        contentId
        contentType
        entityType
        name
        titleGroupId
        confidence
        matchType
      }
      intentSignal {
        classification
        reason
        requiresLlm
      }
      adjacency {
        centerId
        centerName
        sharedContentCount
        adjacencyType
      }
      chatSessionId
      blacklistMessage
      heroImageUrls
    }
  }
`

// Backward compat alias
export { SEARCH_QUERY as AI_SEARCH_CONTENT_QUERY }

export const AI_SEARCH_PLAYLISTS_QUERY = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query AiSearchPlaylists(
    $query: String!
    $topContentIds: [Int!]!
    $topContentScores: [Float!]!
    $playlistLimit: Int
    $language: [String]
  ) {
    aiSearchPlaylists(
      query: $query
      topContentIds: $topContentIds
      topContentScores: $topContentScores
      playlistLimit: $playlistLimit
    ) {
      playlists {
        playlistId
        playlistName
        description
        itemCount
        matchedContentCount
        textRelevance
        score
        previewThumbnailUrls
        previewContentTitles
        creatorDisplayName
        playlist {
          id
          visibility
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
            avatar {
              url
              small
              card
            }
          }
          playlistItems {
            ...PlaylistItemContentFields
          }
        }
      }
      playlistTotalCount
    }
  }
`

// AI_SEARCH_PROFILES_QUERY removed 2026-06-03 — profiles not searched anywhere.

// ── Tile hydration (separate query, network-only for correct language) ──

export const SEARCH_TILE_HYDRATION_QUERY = gql`
  query SearchTileHydration(
    $titleGroupIds: [String]!
    $language: [String]!
    $imageType: [String]!
    $learnerLevelLanguage: String!
  ) {
    contentList(
      where: { titleGroupId: { in: $titleGroupIds } }
      language: $language
    ) {
      content {
        id
        contentId
        titleGroupId
        contentType
        title
        path
        shortDescription
        episodeNumber
        seasonNumber
        instructor
        displayType
        subType
        studio
        isNew
        primaryLanguage
        contentAnalytic {
          votesUpCountTotal
          votesCountTotal
          commentsCountTotal
          thumbPopularityMappingId
        }
        featureMedia {
          duration
          mediaId
          userMediaPosition {
            position
          }
          contentPolicy {
            accessCheck {
              allowed
            }
          }
          mediaAssets {
            language
            mediaReferenceType
            aiMetadata(language: $learnerLevelLanguage) {
              learnerLevel {
                level
              }
            }
          }
        }
        imageContentRelationships(
          language: $language
          types: $imageType
        ) {
          type
          image {
            url
          }
        }
        contentAssetReferences(contentAssetReferences: [{ type: "d6-keyart-no-text" }]) {
          asset {
            renderUrl
          }
          contentAssetReferenceType {
            type
          }
        }
        contentSeries(language: $language) {
          contentId
          title
          path
        }
        contentFeatured(language: $language, content: [{ publishedState: PUBLISHED_NOW }]) {
          featuredType
          content {
            contentId
            contentType
            title
            path
            episodeNumber
            seasonNumber
            contentAnalytic {
              votesUpCountTotal
              votesCountTotal
              commentsCountTotal
              thumbPopularityMappingId
            }
            featureMedia {
              duration
              mediaId
              userMediaPosition {
                position
              }
              contentPolicy {
                accessCheck {
                  allowed
                }
              }
              mediaAssets {
                language
                mediaReferenceType
                aiMetadata(language: $learnerLevelLanguage) {
                  learnerLevel {
                    level
                  }
                }
              }
            }
          }
        }
        episodesCount
        seasonsCount
        courseProgress {
          state
          metrics {
            totalModules
            completedModules
          }
        }
      }
    }
  }
`

// ── Tile references (separate non-blocking query) ───────────────────
// References stay out of SEARCH_TILE_HYDRATION_QUERY on purpose: an inline
// references field was reverted on 2026-06-12 after search timed out
// (e9c4c05b8 / 19c4e2d3a). Fetching them separately means a slow or failed
// references query can never delay or blank tile rendering. Same 11-type
// list as member home's GET_CONTENT (lib/services/content/query.js).

export const SEARCH_TILE_REFERENCES_QUERY = gql`
  query SearchTileReferences(
    $titleGroupIds: [String]!
    $language: [String]!
  ) {
    contentList(
      where: { titleGroupId: { in: $titleGroupIds } }
      language: $language
    ) {
      content {
        id
        contentId
        titleGroupId
        contentType
        references(types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level"]) {
          id
          contentReferenceType {
            type
          }
          contentTo(language: $language) {
            title
            contentType
            contentId
            language
            path
          }
        }
      }
    }
  }
`

// ── Playlist search (searchPlaylists — knowledge graph path) ───────

export const SEARCH_PLAYLISTS_QUERY = gql`
  query SearchPlaylists(
    $query: String
    $language: String
    $limit: Int
    $offset: Int
    $filters: SearchFilters
    $sessionId: ID
    $previousChipTexts: [String!]
    $previousQuery: String
    $previousPlaylistIds: [ID!]
  ) {
    searchPlaylists(
      query: $query
      language: $language
      limit: $limit
      offset: $offset
      filters: $filters
      sessionId: $sessionId
      previousChipTexts: $previousChipTexts
      previousQuery: $previousQuery
      previousPlaylistIds: $previousPlaylistIds
    ) {
      playlistResultIds
      playlistScores
      playlistMatchReasons {
        type
        label
        weight
      }
      totalCount
      chatSessionId
    }
  }
`

// ── Playlist hydration (for searchPlaylists IDs) ───────────────────

export const DISCOVER_PLAYLIST_HYDRATION_QUERY = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query DiscoverPlaylistHydration($ids: [ID!]!, $language: [String]!, $learnerLevelLanguage: String!) {
    playlistList(where: { id: { in: $ids } }) {
      playlists {
        id
        playlistName
        playlistType
        visibility
        description
        itemCount
        followerCount
        isFollowing
        userProfile {
          uuid
          displayName
          avatar {
            url
            small
            card
          }
        }
        playlistItems {
          ...PlaylistItemContentFields
          content(language: $language) {
            contentFeatured(language: $language, content: [{ publishedState: PUBLISHED_NOW }]) {
              featuredType
              content {
                contentId
                contentType
                title
                path(format: RELATIVE)
                episodeNumber
                seasonNumber
                contentAnalytic {
                  votesUpCountTotal
                  votesCountTotal
                  commentsCountTotal
                  thumbPopularityMappingId
                }
                featureMedia {
                  duration
                  mediaId
                  userMediaPosition {
                    position
                  }
                  contentPolicy {
                    accessCheck {
                      allowed
                    }
                  }
                  mediaAssets {
                    language
                    mediaReferenceType
                    aiMetadata(language: $learnerLevelLanguage) {
                      learnerLevel {
                        level
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

// ── Overview stream subscription ────────────────────────────────────

export const OVERVIEW_STREAM_SUBSCRIPTION = gql`
  subscription SearchOverview($input: SearchOverviewStreamSubscribeInput!) {
    searchOverviewStream(input: $input) {
      chatSessionId
      turnId
      textChunk
      statusText
      refinementSuggestions
      structuredRefinements {
        text
        refinementType
        query
        level
        duration
        entityId
        seriesContentId
        contentId
        standout
        standoutReason
        standoutTier
      }
      executionTime
      isFinal
      success
      errors
      decisionId
    }
  }
`
